import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTAButtons from "@components/new-ui/CTAButtons";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import NewLayout from "@components/new-ui/Layout";
import AwardSection from "@components/new-ui/partner-program/AwardSection";
import GallaboxAtAGlance from "@components/new-ui/partner-program/GallaboxAtAGlance";
import PartnerSuccessStories from "@components/new-ui/partner-program/PartnerSuccessStories";
import { PartnerEcosystemItem, partnerEcosystemItems } from "@components/new-ui/partner-program/partnerEcosystemData";
import StepCard from "@components/StepCard";
import Image from "next/image";
import { useEffect, useState } from "react";
import { LuArrowUpRight } from "react-icons/lu";
import { getAffiliateId, buildCalendlyUrl } from "@utils/campaign-tracker";

const featureInfo1: FeatureInfo = {
    mainTitle: "Who should partner with us?",
    imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/team.png",
    featureInfoList: [
        "Digital marketing agencies – Add WhatsApp automation to your stack",
        "SaaS resellers – Expand your product portfolio",
        "Tech implementers – Deploy enterprise messaging solutions",
        "Business consultants – Modernize client communication",
    ],
};

const productCardData: Array<Product> = [
    {
        title: "Earn more. For longer",
        description:
            "Earn recurring commissions on every client you bring. Hit key milestones to unlock higher commission tiers. You continue to earn for five full years from the first activation.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/dollar.svg",
    },
    {
        title: "Build once. Earn always",
        description: "Offer chatbot setup, onboarding, and implementation services and retain 100% of the service fees.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/pipleline.svg",
    },
    {
        title: "Command your market",
        description: "We’ll co-host events, share co-branded sales kits, and boost your pipeline — with a dedicated manager for strategic planing and ongoing support.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/campaign.svg",
    },
    {
        title: "Access our product roadmap",
        description: "Our team ships new features and product enhancements — monthly. Get exclusive access to our feature releases, product roadmap, and help shape our product.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-call.svg",
    },
    {
        title: "Get the inside edge",
        description: "Enjoy lifetime product access, dedicated coaching, marketing support, and priority tech help — so you're always one step ahead.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/info.svg",
    },
    {
        title: "Exclusive demo account",
        description: "Get access to a Gallabox sandbox account to enhance your sales pitch and product demos to leave a lasting impression on your customers.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/video-group.svg",
    },
];

const faqData: FAQSectionProps = {
    title: "Got questions? Perhaps these FAQs can help!",
    faqItems: [
        {
            question: "How long do partners earn commissions?",
            answer: "You receive commissions for 5 years from a client’s initial activation date.",
        },
        {
            question: "How do commission tiers work?",
            answer: (
                <>
                    Your commission rate increases as you onboard more clients. No timeline restrictions – climb tiers at your pace. Reach out to our partner team for tier details.
                </>
            ),
        },
        {
            question: "If the commission rate increases, will it apply to earlier clients for whom a lower percentage was applicable?",
            answer: "Commission increases apply only to new clients acquired after crossing the respective threshold. Commissions for your earlier clients will remain at the previously applicable rate.",
        },
        {
            question: "How are partner commissions paid?",
            answer: (
                <p>
                    Retain commissions upfront! Example:
                    <li>Bill your client directly</li>
                    <li>Use your partner discount code at activation</li>
                    <li>Pay Gallabox the discounted amount and keep the difference as commission</li>
                </p>
            ),
        },
        {
            question: "What does Gallabox implementation cost?",
            answer: "Between ₹15,000 – ₹45,000 if we handle it. *You keep 100% of the implementation/consulting fees when you do it.",
        },
        {
            question: "When are Instagram automations coming?",
            answer: "Within 6 months.",
        },
        {
            question: "What integrations are available?",
            answer: (
                <>
                    Please check this link:{" "}
                    <a href="https://gallabox.com/whatsapp-integrations" className="px-1 text-blue-700">
                        https://gallabox.com/whatsapp-integrations
                    </a>
                </>
            ),
        },
        {
            question: "Can you build custom integrations?",
            answer: "Yes – we’ll share API docs for you to build, or handle it for nominal charges.",
        },
        {
            question: "How is AI chatbot pricing calculated?",
            answer: (
                <p>
                    Cost depends on the characters processed.{" "}
                    <a href="https://mailto:partners@gallabox.com/" className="px-1">
                        Contact us
                    </a>
                    , for session estimates.
                </p>
            ),
        },
        {
            question: "Is exclusivity available for partners?",
            answer: "At this stage, we are not offering exclusivity. However, it may be considered for partners bringing significant volume.",
        },
        {
            question: "Does Gallabox allow a post-paid model?",
            answer: "No, we strictly avoid it and we recommend our partners to follow the same.",
        },
        {
            question: "What are the next steps for the partners after signing the agreement?",
            answer: "Upon signing the partnership agreement, we will onboard you on our Partner CRM and set up enablement sessions.",
        },
    ],
};

const partnerPageData = [
    {
        title: "Apply",
        description: "Complete our partner application (5 minutes)",
    },
    { title: "Onboard", description: "Join kickoff training and get your demo workspace" },
    {
        title: "Launch",
        description: "Start selling with proven playbooks and co-marketing support",
    },
];

const PARTNERSHIP_CALENDLY_URL = "https://calendly.com/d/cn43-jvp-q8f/gallabox-partnership-discussion?hide_event_type_details=1";

const PartnerProgramPage: React.FC = () => {
    const filters = ["All", "India", "Middle East", "Africa", "USA"];
    const [filteredData, setFilteredData] = useState<Array<PartnerEcosystemItem>>(partnerEcosystemItems);

    const [currIndex, setCurrIndex] = useState<number>(0);
    const [isCalendlyHighlighted, setIsCalendlyHighlighted] = useState<boolean>(false);
    const [calendlyUrl, setCalendlyUrl] = useState(PARTNERSHIP_CALENDLY_URL);

    const itemsPerRow = 3;
    const initialRows = 2;
    const [visibleCount, setVisibleCount] = useState(initialRows * itemsPerRow);

    const handleBtnSelect = (title: string, index: number) => {
        setCurrIndex(index);
        setVisibleCount(initialRows * itemsPerRow);

        if (title !== "All") {
            const data = partnerEcosystemItems.filter((item) => item.subtext.includes(title));
            setFilteredData(data);
        } else {
            setFilteredData(partnerEcosystemItems);
        }
    };

    const handleGetStartedClick = () => {
        const calendlyWidget = document.getElementById("calendly-widget");
        if (calendlyWidget) {
            calendlyWidget.scrollIntoView({ behavior: "smooth", block: "center" });

            setIsCalendlyHighlighted(true);

            setTimeout(() => {
                setIsCalendlyHighlighted(false);
            }, 2000);
        }
    };

    useEffect(() => {
        const affiliateId = getAffiliateId();
        setCalendlyUrl(buildCalendlyUrl(PARTNERSHIP_CALENDLY_URL, affiliateId));
    }, []);

    useEffect(() => {
        const scriptId = "calendly-widget-script";
        const isScriptAlreadyPresent = document.getElementById(scriptId);

        if (!isScriptAlreadyPresent) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://assets.calendly.com/assets/external/widget.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    return (
        <NewLayout
            title={"Join the Gallabox Partner Program & Earn Recurring Revenue"}
            description={" Become a Gallabox partner and earn by helping businesses grow with WhatsApp. Enjoy recurring commissions and full support to scale your referrals."}
            canonical={"https://gallabox.com/whatsapp-partner-program"}
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/partnership/partner-program-og.png" }}
        >
            <section className="lg:pt-16 pt-10" id="talkToOurTeam">
                <header className="lg:max-w-7xl lg:mx-auto px-5 w-full flex flex-col lg:py-16 pb-16 gap-16">
                    <div className="flex flex-col lg:flex-row lg:py-5 lg:gap-0 gap-8">
                        <div className="lg:w-1/2 w-full flex flex-col justify-start lg:gap-0 gap-4">
                            <div>
                                <p className="text-base font-semibold mb-3 md:block hidden" style={{ color: "#3364FA" }}>
                                    PARTNER WITH GALLABOX
                                </p>
                                <h1 className="lg:text-6xl text-5xl  font-bold lg:mb-6" style={{ fontFamily: "Degular Variable" }}>
                                    Stop trading hours for dollars. Start earning while your clients grow.
                                </h1>
                                <p className="text-xl font-normal lg:mb-8 leading-8 lg:w-3/4">
                                    Help businesses automate appointments, sales, and support on WhatsApp and Instagram — earn recurring commissions while doing it.
                                </p>
                            </div>
                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex items-center border border-solid rounded px-3 py-2" style={{ background: "#E6F3FF", borderColor: "#CCE6FF" }}>
                                    <Image width={189} height={12} src="https://assets.gallabox.com/gb-home/partnership/meta.png" alt="meta-logo" />
                                </div>
                            </div>
                            <CTAButtons primaryButtonText="Get Started" onPrimaryClick={handleGetStartedClick} />
                        </div>
                        <div className="md:w-1/2">
                            <div
                                id="calendly-widget"
                                className="calendly-inline-widget"
                                style={{
                                    height: "650px",
                                    overflow: "hidden",
                                    boxShadow: isCalendlyHighlighted
                                        ? "0px 0px 0px 4px rgba(51, 100, 250, 0.5), 0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10)"
                                        : "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10)",
                                    borderRadius: "8px",
                                    transition: "box-shadow 0.3s ease-in-out",
                                }}
                                data-url={calendlyUrl}
                            />
                        </div>
                    </div>

                    <div className="w-full">
                        <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                        <ClientLogos />
                    </div>
                </header>
            </section>
            <GallaboxAtAGlance />
            <ProductSection productCardData={productCardData} title="Why partners choose us" gridCols="md:grid-cols-3" />
            <PartnerSuccessStories />
            <AwardSection />
            <FeatureInfoCard {...featureInfo1} />

            <section className="max-w-7xl mx-auto px-5 py-20">
                <h3 className="text-6xl font-semibold mb-5" style={{ fontFamily: "Degular Display" }}>
                    Explore our partner ecosystem
                </h3>
                <p className="text-xl mb-16">
                    Find a certified Gallabox partner in your region to help implement our all-in-one WhatsApp automation platform built for fast-growing small-to-medium-sized
                    businesses.
                </p>
                <div className="flex-row lg:block hidden pb-0 mb-4 rounded-xl">
                    {filters.map((filter, index) => (
                        <button
                            key={filter}
                            onClick={() => handleBtnSelect(filter, index)}
                            style={{
                                borderRadius: "4px",
                                border: "1px solid var(--blue-200, #CBD5E0)",
                                background: index === currIndex ? "#3364FA" : "#F7FAFC",
                                color: index === currIndex ? "white" : "black",
                                margin: "5px 5px 5px 5px",
                                outline: "none",
                            }}
                        >
                            <div className="flex justify-center">
                                <p style={{ margin: "10px 30px" }}>{filter}</p>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {filteredData.slice(0, visibleCount).map((item, index) => {
                        if (item.link) {
                            return (
                                <a key={item.link} href={item.link} style={{ textDecoration: "none" }} target="blank">
                                    <div key={index} className="bg-white rounded-md shadow">
                                        <Image width={400} height={164} src={item.thumbnail} alt={`${item.title} logo`} className="mb-2" />
                                        <div className="p-4">
                                            <div className="font-bold text-gray-700">{item.title}</div>
                                            <div className="text-base text-gray-500">{item.subtext}</div>
                                            <div className="text-base text-gray-500 h-40">{item.description}</div>
                                        </div>
                                    </div>
                                </a>
                            );
                        }
                        return (
                            <div key={index} className="bg-white rounded-md shadow">
                                <Image width={400} height={164} src={item.thumbnail} alt={`${item.title} logo`} className="mb-2" />
                                <div className="p-4">
                                    <div className="font-bold text-gray-700">{item.title}</div>
                                    <div className="text-base text-gray-500">{item.subtext}</div>
                                    <div className="text-base text-gray-500 h-40">{item.description}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {visibleCount < filteredData.length && (
                    <div className="text-center mt-8">
                        <button
                            onClick={() => setVisibleCount((prev) => prev + itemsPerRow * 2)}
                            className="text-lg px-5 py-3 border border-solid rounded"
                            style={{ color: "#3364FA" }}
                        >
                            Load More
                        </button>
                    </div>
                )}
            </section>
            <div className="mt-20">
                <StepCard
                    cards={partnerPageData}
                    title="Start earning in 3 simple steps"
                    description="No technical expertise required. We'll guide you from signup to your first client."
                />
            </div>

            <FAQSection faqData={faqData} />
            <section
                className={`${container_classname} flex md:flex-row flex-col md:rounded-xl justify-between md:items-center py-10 md:py-6 pr-10 my-20`}
                style={{
                    background: "var(--Grad-7, linear-gradient(265deg, #396AFF -7.73%, #00238D 61.31%, #000 102.16%))",
                }}
            >
                <div className="md:px-10 flex flex-col">
                    <p className="text-5xl font-semibold text-white md:pb-2 pb-2" style={{ fontFamily: "Degular Display" }}>
                        Ready to build recurring revenue?
                    </p>
                    <p className="text-white pb-10">Join our partner program and help businesses transform customer conversations into growth. </p>
                    <a
                        href="#talkToOurTeam"
                        className="
                                    mb-4 md:mb-0  md:px-8 px-5 md:py-5 py-6 gap-2 mt-5 text-black md:text-xl md:font-semibold bg-white rounded-md hover:no-underline focus:outline-none hover:shadow-2xl flex items-center w-fit
                                "
                    >
                        Apply Now
                        <LuArrowUpRight />
                    </a>
                </div>
                <div>
                    <Image width={300} height={300} src={"https://assets.gallabox.com/gb-home/partnership/become-partner.png"} alt={"becomepartner"} />
                </div>
            </section>
            <AwardsSection />
        </NewLayout>
    );
};

export default PartnerProgramPage;
